import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    TextField,
    Grid,
    Typography,
    Divider,
    FormControl,
    FormLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import {
    editServiceWithoutActRequest,
} from "../../../../redux/actions";
import {
    PageContainer,
    Form,
    FormSection,
    CustomDatePicker,
    BasicTable,
    ConditionalContainer,
} from "../../../../components";

import { RequestStateChecker } from "../../../../functions";
import CreateProtocol from "../../../Protocol/Popups/CreateProtocol";
import CreateExpert from "../../../Protocol/Popups/CreateExpert";
import EmployeeField from "../../../../components/Relations/EmployeeField";
import ResultsAndProtocolsSection from "../../../../sections/ResultsAndProtocolsSection";
import useReload from "../../../../hooks/useReload";
import CreateResult from "../../../Result/Popups/CreateResult";
import { addProtocol } from "../../../../redux/actions/protocolActions";
import EditResultAndProtocolSection from "../../../../sections/EditResultAndProtocolSection";
import useLoader from "../../../../hooks/useLoader";
import { protocolCRUD } from "../../../../http";
import { toUIDate } from "../../../../functions/dateHelpers";
import { formatDate } from "../../../../functions/formatters";
import ResearchListField from "../../../../components/Relations/ResearchListField";
import { SERVICE_TYPE_PAYED } from "../../../../redux/constants/serviceTypeConstants";
import { mapReferralSpecificationsForSubmit } from "../../../../functions/viewDataMap";

const uniqueBy = (items, getKey) => {
    const seen = new Set();
    return (items || []).filter((item) => {
        const key = getKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const specificationUniqueKey = (specification) => {
    const serviceId = specification?.serviceId ?? specification?.service?.id ?? "";
    const objectId = specification?.objectId ?? specification?.object?.id ?? "";
    const serviceKindId = specification?.serviceKindId ?? specification?.serviceKind?.id ?? "";
    const unitId = specification?.unitId ?? specification?.unit?.id ?? "";
    const scheduleId = specification?.scheduleId ?? specification?.schedule?.id ?? "";
    const price = specification?.price ?? "";
    const count = specification?.count ?? "";

    return [serviceId, objectId, serviceKindId, unitId, scheduleId, price, count].join("|");
};

export const ServiceWithoutActRequestInfoSection = ({
    setData,
    setProtocolAddOpen,
    protocolAddOpen,
    data,
    results,
    fetch,
    protocols,
    expertResults,
    setExpertAddOpen,
    expertAddOpen,
    createResultOpen,
    setCreateResultOpen,
    validationError,
}) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const {
        start: startSubmit,
        stop: stopSubmit,
        loading: submitLoading,
    } = useLoader(false);

    const [loading, setLoading] = useState(false);

    const [request, setRequest] = useState(data);

    const { serviceWithoutActRequest } = useSelector(
        (s) => s.serviceWithoutActRequest
    );

    const stateCode = serviceWithoutActRequest?.state?.code?.toLowerCase();
    const state = new RequestStateChecker(stateCode);

    const [responsiblePerformerId, setResponsiblePerformerId] = useState({});
    const [specifications, setSpecifications] = useState([]);
    const [referralSpecifications, setReferralSpecifications] = useState([]);

    const [objects, setObjects] = useState([]);
    const [responsiblePerformer, setResponsiblePerformer] = useState({});
    const [departureDate, setDepartureDate] = useState(new Date());
    const [editResultOpen, setEditResultOpen] = useState(false);
    const [editResultId, setEditResultId] = useState(null);
    const [editProtocolOpen, setEditProtocolOpen] = useState(false);
    const [editProtocol, setEditProtocol] = useState(null);

    const { reload, reloadValue } = useReload();

    const handleChangeObject = useCallback(
        (objectKey, value) => {
            setData({
                ...data,
                [objectKey]: value,
                [`${objectKey}Id`]: value?.id ?? null,
            });
        },
        [data]
    );

    const handleEditResultOpen = async (resultId) => {
        setEditResultId(resultId);
        setEditResultOpen(true);
    }

    const handleSpecialistUpdate = async (e) => {
        e.preventDefault();

        setLoading(true);

        // Нормализуем спецификации перед сохранением
        const normalizedData = {
            ...request,
            reponsiblePerformerId: responsiblePerformerId,
            objects: uniqueBy(request?.objects || [], (item) => item?.id),
            specifications: uniqueBy(request?.specifications || [], specificationUniqueKey),
            referralSpecifications: mapReferralSpecificationsForSubmit(data?.referralSpecifications || []),
        };

        console.log({ data: normalizedData });

        // Updating
        await dispatch(editServiceWithoutActRequest(normalizedData));

        setLoading(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
    };
    
    const handleChange = useCallback((key, value) => {
        setData({ ...data, [key]: value })
      }, [data]);

    const renderDateField = (key, label, xs = 12) => {
        return (
            <Grid item xs={xs}>
                <FormControl component="fieldset">
                    {label && <FormLabel component="legend">{label}</FormLabel>}
                    <TextField
                        type={"date"}
                        margin="normal"
                        value={toUIDate(data[key])}
                        label="Дата"
                        onChange={(e) => handleChange(key, e.target.value)}
                        fullWidth
                        id={key}
                        name={key}
                    />
                </FormControl>
            </Grid>
        );
    };

    // useEffect(() => {
    //   dispatch(getServiceWithoutActRequestByKey(requestId)).then(setRequest);
    // }, [dispatch, requestId]);

    useEffect(() => {
        // Нормализуем спецификации для удаления дубликатов
        const normalizedSpecifications = uniqueBy(request?.specifications || [], specificationUniqueKey);
        const normalizedObjects = uniqueBy(request?.objects || [], (item) => item?.id);
        
        setObjects(normalizedObjects);
        setSpecifications(normalizedSpecifications);
        setReferralSpecifications(request.referralSpecifications);
        setResponsiblePerformer(request.setResponsiblePerformer);
        setDepartureDate(request.departureDate);
        setResponsiblePerformer(request.reponsiblePerformer);

    }, [request]);

    useEffect(() => {
        // Нормализуем спецификации при обновлении из Redux store
        if (serviceWithoutActRequest?.specifications) {
            const normalizedSpecifications = uniqueBy(serviceWithoutActRequest.specifications, specificationUniqueKey);
            const normalizedObjects = uniqueBy(serviceWithoutActRequest?.objects || [], (item) => item?.id);
            setRequest({ 
                ...request, 
                ...serviceWithoutActRequest,
                specifications: normalizedSpecifications,
                objects: normalizedObjects
            });
        } else {
            setRequest({ ...request, ...serviceWithoutActRequest });
        }
    }, [serviceWithoutActRequest]);

    const handleEditProtocolOpen = async (protocolId) => {
        await startSubmit();

        const protocol = await protocolCRUD.get(protocolId);
        await setEditProtocol(protocol);
        await setEditProtocolOpen(true);

        await stopSubmit();
    }

    return (
        <>
            <Form onSubmit={handleSubmit}>
                <PageContainer>
                    <FormSection heading="Заявка" mb={4}>
                        {stateCode == 'declined' && <Grid item xs={12}>
                            <label>Комментарий: {serviceWithoutActRequest?.comment}</label>
                        </Grid>}
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <TextField
                                    label="Номер заявки"
                                    type="number"
                                    value={serviceWithoutActRequest?.number}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="Сводная заявка"
                                    type="text"
                                    value={serviceWithoutActRequest?.contractRequestId}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Автор заявки"
                                    type="text"
                                    value={serviceWithoutActRequest?.createdBy?.fullName}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <CustomDatePicker
                                            label="Дата заявки"
                                            value={serviceWithoutActRequest?.createdAt}
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </FormSection>

                    <FormSection heading="Журналы и протоколы" mb={4}>
                        <ResultsAndProtocolsSection
                            results={results}
                            protocols={protocols}
                            expertResults={expertResults}
                            onResultClick={async (_) => {
                                await handleEditResultOpen(_);
                            }}
                            onProtocolClick={async (_) => {
                                await handleEditProtocolOpen(_);
                            }}
                            onExpertResultClick={async (_) => {
                                // TODO: implement
                            }}
                            resultsReloadValue={reloadValue}
                            fetch={fetch}
                            startLoading={async () => {
                                //await startSubmit();
                            }}
                            stopLoading={async () => {
                                //await stopSubmit();
                            }}
                        />
                    </FormSection>

                    <FormSection mb={4}>
                        <Typography component="h4" align="left" variant="h6">
                            Объекты ({objects?.length || 0})
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <BasicTable
                                    cells={[
                                        { id: "id", label: t("object.tableItem.id") },
                                        { id: "name", label: t("object.tableItem.name") },
                                        {
                                            id: "locality",
                                            label: t("object.tableItem.locality"),
                                            render: (row) => row?.locality?.name,
                                        },
                                        {
                                            id: "district",
                                            label: t("object.tableItem.district"),
                                            render: (row) => row?.district?.name,
                                        },
                                        {
                                            id: "city",
                                            label: t("object.tableItem.city"),
                                            render: (row) => row?.city?.name,
                                        },
                                        {
                                            id: "addressLine",
                                            label: t("object.tableItem.addressLine"),
                                        },
                                    ]}
                                    rows={objects}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </FormSection>
                    <FormSection mb={4}>
                        <Typography component="h4" align="left" variant="h6">
                            Услуги ({specifications?.length || 0})
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <BasicTable
                                    cells={[
                                        { id: "id", label: t("specification.tableItem.id") },
                                        {
                                            id: "object",
                                            label: t("specification.tableItem.object"),
                                            render: ({ object }) => object?.name,
                                        },
                                        {
                                            id: "serviceKind",
                                            label: t("specification.tableItem.serviceKind"),
                                            render: ({ serviceKind }) => serviceKind?.name,
                                        },
                                        {
                                            id: "service",
                                            label: t("specification.tableItem.service"),
                                            render: ({ service }) => service?.name,
                                        },
                                        {
                                            id: "unit",
                                            label: t("specification.tableItem.unit"),
                                            render: ({ unit }) => unit?.name,
                                        },
                                        {
                                            id: "schedule",
                                            label: t("specification.tableItem.schedule"),
                                            render: ({ schedule }) => schedule?.name,
                                        },
                                        {
                                            id: "count",
                                            label: t("specification.tableItem.quantity"),
                                        },
                                        { id: "price", label: t("specification.tableItem.price") },
                                    ]}
                                    rows={specifications}
                                    disabled
                                />
                                {state.isResearch() && (
                                    <ResearchListField
                                        type={SERVICE_TYPE_PAYED}
                                        label="Список Исследований *"
                                        error={validationError}
                                        value={data.referralSpecifications || []}
                                        onChange={(val) => handleChange("referralSpecifications", val)}
                                    />
                                )}
                                {state.isCompleted() && (
                                    <ResearchListField
                                        type={SERVICE_TYPE_PAYED}
                                        label="Список Исследований"
                                        disabled={true}
                                        mode="readonly"
                                        value={data.referralSpecifications || []}
                                        onChange={(val) => handleChange("referralSpecifications", val)}
                                    />
                                )}
                                {state.isResearch() && renderDateField(
                                    "researchEndDateTime",
                                    "Дата окончания исследования * (цены тарификатора зависят от этой даты)",
                                    12,
                                    {}
                                )}
                                {state.isCompleted() && (
                                    <label>
                                        Дата завершения исследования: {formatDate(data.researchEndDateTime)}
                                    </label>
                                )}
                            </Grid>
                        </Grid>
                    </FormSection>
                    <FormSection heading="Лаборатория">
                        <Grid container spacing={2}>
                            <Grid item xs={5}>
                                <TextField
                                    label="Лаборатория"
                                    value={request?.department?.name}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    label="Зав. лабораторией"
                                    value={request?.responsible?.fullName}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <CustomDatePicker
                                    label="Дата выезда"
                                    value={departureDate}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </FormSection>

                    <ConditionalContainer
                        condition={
                            state.isProcessing() || state.isResearch() || state.isFinished()
                        }
                    >
                        <Divider style={{ margin: "50px 0px" }} />
                        <FormSection heading="Назначение ответственных за оказание услуги">
                            <Grid container spacing={2}>
                                <Grid item xs={8}>
                                    <EmployeeField
                                        label={"Лаборант"}
                                        fullWidth
                                        object={data?.reponsiblePerformer}
                                        value={data?.reponsiblePerformerId}
                                        onChange={(performer) => {
                                            handleChangeObject('reponsiblePerformer', performer);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            {state.isProcessing() && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <LoadingButton
                                            loading={loading}
                                            onClick={handleSpecialistUpdate}
                                        >
                                            Сохранить
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            )}
                        </FormSection>
                    </ConditionalContainer>
                </PageContainer>
                <EditResultAndProtocolSection
                    editResultOpen={editResultOpen}
                    setEditResultOpen={setEditResultOpen}
                    editProtocolOpen={editProtocolOpen}
                    setEditProtocolOpen={setEditProtocolOpen}
                    editResultId={editResultId}
                    editProtocol={editProtocol}
                    startLoading={async () => {
                        await startSubmit();
                    }}
                    stopLoading={async () => {
                        await stopSubmit();
                    }}
                    onEditResultSubmitted={async (_) => {
                        reload();
                    }}
                    onEditProtocolSubmitted={async (_) => {
                        await fetch();
                    }}
                    results={results}
                    expertResults={expertResults}

                />
                {createResultOpen && (
                    <CreateResult
                        action={"createByServiceWithoutActRequest"}
                        title={t("resultList.createByReferralText")}
                        onClose={() => setCreateResultOpen(false)}
                        onSubmit={async (response) => {
                            await fetch();
                        }}
                        referral={request}
                    />
                )}
                {protocolAddOpen && (
                    <CreateProtocol
                        action={"createByServiceWithoutActRequest"}
                        open={protocolAddOpen}
                        initNumber={data.number}
                        title={`Создание протокола для заявки без акта №${data.number}?`}
                        onClose={() => setProtocolAddOpen(false)}
                        onSubmit={(data) => {
                            return dispatch(
                                addProtocol(data, { sort: { id: { operator: "asc" } } })
                            ).then(fetch);
                        }}
                        setOpen={setProtocolAddOpen}
                        request={data}
                    />
                )}
                 {expertAddOpen && (
                <CreateExpert open={expertAddOpen}
                    initNumber={data.number}
                    setOpen={setExpertAddOpen}
                    onClose={() => setExpertAddOpen(false)}
                    relations={{
                        referral: data
                    }
                    }
                    onSubmit={async (data) => {
                        setExpertAddOpen(false);
                        return true;
                    }} />
            )}

            </Form>
        </>
    );
};
